import { computed } from 'vue';
import store from '@/store';
import { config } from '@/config.js';
import { useQueryList } from '@/composables/common/useQueryList';
import { useToast } from '@/composables/common/useToast';

/**
 * useCompanias — Listado de compañías del teatro activo.
 *
 * Mismo patrón que `useAbonados` / `useRegistrosVerifactu`: `useQueryList`
 * abstrae filtros + paginación + sincronización con la URL, y aquí solo
 * viven el fetcher contra la API, las columnas de la tabla y las acciones
 * de fila.
 *
 * El listado NO depende de una temporada (`lista_id`): la compañía pertenece
 * al teatro, así que `useQueryList` usa su fuente por defecto ('route') y el
 * `lista_id` resultante viaja como `undefined` (el backend lo ignora).
 *
 * @returns {Object} state + actions para el Index de compañías
 */
export function useCompanias() {
    const toast = useToast();

    // ==================== COMPUTED ====================
    const teatro = computed(() => store.getters.getTeatro);

    // ==================== LIST QUERY (useQueryList) ====================
    const list = useQueryList({
        fetcher: async params => {
            // `teatro_id` lo consume la ruta de la API como merge tag
            // (%teatro_id%); useQueryList no lo conoce, lo añadimos aquí.
            const res = await store.dispatch(
                config.getVuexAlias('back.teatro.compania-eventos.index'),
                {
                    filters: params.filters,
                    teatro_id: teatro.value?.id,
                }
            );

            // El backend devuelve { data: [...], meta: {...} }.
            return res.data || { data: [], meta: {} };
        },
    });

    const {
        filters,
        data: companias,
        loading,
        meta,
        fetch: getCompanias,
    } = list;

    // ==================== COLUMNS ====================
    const columns = [
        { key: 'nif', label: 'NIF', field: 'nif', centered: true, class: 'tw:w-24' },
        { key: 'nombre', label: 'Nombre', field: 'nombre', class: 'tw:w-32', truncate: true },
        { key: 'direccion', label: 'Dirección', field: 'direccion', class: 'tw:w-40', truncate: true },
        { key: 'ciudad', label: 'Ciudad', field: 'ciudad', class: 'tw:w-28', truncate: true },
        { key: 'cp', label: 'CP', field: 'cp', centered: true, class: 'tw:w-20' },
        { key: 'cuenta', label: 'Cuenta', field: 'cuenta', class: 'tw:w-36', truncate: true },
        { key: 'email', label: 'Email', field: 'email', class: 'tw:w-40', truncate: true },
        { key: 'telefono', label: 'Teléfono', field: 'telefono', centered: true, class: 'tw:w-28' },
        { key: 'verifactu', label: 'Verifactu', centered: true, class: 'tw:w-24' },
        { key: 'opciones', label: 'Opciones', centered: true, class: 'tw:w-24' },
    ];

    // ==================== HELPERS ====================
    /**
     * Una compañía tiene Verifactu operativo solo cuando el backend le ha
     * guardado identificador Y token de empresa.
     */
    function hasVerifactuConfigurado(compania) {
        const id = compania?.verifactu_empresa_id;
        const token = compania?.verifactu_empresa_token;
        const idValido = id !== null && id !== undefined && String(id).trim() !== '';
        const tokenValido = token !== null && token !== undefined && String(token).trim() !== '';
        return idValido && tokenValido;
    }

    // ==================== ACTIONS ====================
    async function deleteCompania(companiaId) {
        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.teatro.compania-eventos.borrar'),
                {
                    filters: {},
                    teatro_id: teatro.value?.id,
                    company_id: companiaId,
                }
            );
            toast.success(res.data.message);
            await getCompanias();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Error al eliminar la compañía');
        }
    }

    // ==================== PUBLIC API ====================
    return {
        filters,
        companias,
        loading,
        meta,
        columns,
        getCompanias,
        deleteCompania,
        hasVerifactuConfigurado,
    };
}
